import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FactoryInfo } from '@/mocks/supplierNetwork';

interface FactoryCardProps {
  factory: FactoryInfo;
  index: number;
}

function SectionTitle({ icon, children }: { icon: string; children: React.ReactNode }) {
  return (
    <h4 className="flex items-center gap-2 text-sm md:text-base font-semibold text-ink-700 mb-3">
      <span className="w-7 h-7 rounded-md bg-warm-50 flex items-center justify-center shrink-0">
        <i className={`${icon} text-warm-500 text-sm`} />
      </span>
      {children}
    </h4>
  );
}

export default function FactoryCard({ factory, index }: FactoryCardProps) {
  const { t } = useTranslation('supplierNetwork');
  const scrollRef = useRef<HTMLDivElement>(null);

  const images = [
    { image: factory.heroImage, labelKey: 'gallery.overview' },
    ...factory.gallery,
  ];

  const [activeIndex, setActiveIndex] = useState(0);
  const active = images[activeIndex];

  const scrollBy = (dir: number) => {
    scrollRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  return (
    <article
      id={factory.id}
      className="bg-white border border-ink-100 rounded-2xl overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center gap-4 px-6 md:px-8 py-6">
        <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-ink-700 flex items-center justify-center shrink-0">
          <i className={`${factory.icon} text-white text-xl md:text-2xl`} />
        </div>
        <div>
          <span className="text-xs font-medium text-warm-500 uppercase tracking-[0.15em] block">
            {t('factory.label')} {String(index + 1).padStart(2, '0')}
          </span>
          <h3 className="text-xl md:text-2xl font-bold text-ink-700 leading-tight">
            {t(factory.nameKey)}
          </h3>
        </div>
        <span className="ml-auto hidden sm:inline-flex text-xs md:text-sm text-ink-500 bg-cream-50 border border-ink-100 rounded-full px-3 py-1.5 whitespace-nowrap">
          {t(factory.taglineKey)}
        </span>
      </div>

      {/* Main image box */}
      <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden bg-ink-100">
        <img
          src={active.image}
          alt={t(active.labelKey)}
          className="w-full h-full object-cover object-center transition-opacity duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900/60 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
          <span className="text-xs md:text-sm font-medium text-white/90 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5 whitespace-nowrap">
            {t(active.labelKey)}
          </span>
        </div>
      </div>

      {/* Thumbnail strip — horizontal scroll */}
      <div className="px-4 md:px-6 py-4 md:py-6 border-b border-ink-100">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-ink-400 uppercase tracking-[0.15em]">
            {t('gallery.title')}
          </span>
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/90 border border-ink-200 flex items-center justify-center text-ink-700 hover:bg-warm-500 hover:text-white hover:border-warm-500 transition-colors cursor-pointer shadow-sm"
            aria-label={t('gallery.scrollLeft')}
          >
            <i className="ri-arrow-left-s-line text-lg md:text-xl" />
          </button>

          <div
            ref={scrollRef}
            className="flex gap-3 overflow-x-auto scroll-smooth snap-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden px-10 md:px-12"
          >
            {images.map((img, i) => {
              const isActive = i === activeIndex;
              return (
                <button
                  key={img.image}
                  type="button"
                  onClick={() => setActiveIndex(i)}
                  className={`relative shrink-0 w-40 md:w-48 aspect-[4/3] rounded-xl overflow-hidden snap-start cursor-pointer transition-all ${
                    isActive
                      ? 'ring-2 ring-warm-500 ring-offset-2 ring-offset-white'
                      : 'opacity-80 hover:opacity-100'
                  }`}
                >
                  <img
                    src={img.image}
                    alt={t(img.labelKey)}
                    className="w-full h-full object-cover object-center"
                    loading="lazy"
                  />
                  <span className="absolute bottom-0 left-0 right-0 px-2.5 py-1.5 bg-ink-900/60 text-xs text-white/80 truncate">
                    {t(img.labelKey)}
                  </span>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => scrollBy(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/90 border border-ink-200 flex items-center justify-center text-ink-700 hover:bg-warm-500 hover:text-white hover:border-warm-500 transition-colors cursor-pointer shadow-sm"
            aria-label={t('gallery.scrollRight')}
          >
            <i className="ri-arrow-right-s-line text-lg md:text-xl" />
          </button>
        </div>
      </div>

      {/* Text content */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 px-6 md:px-8 py-6 md:py-8">
        {/* Introduction */}
        <div>
          <SectionTitle icon="ri-information-line">{t('factory.introduction')}</SectionTitle>
          <p className="text-sm text-ink-500 leading-relaxed mb-4">{t(factory.historyKey)}</p>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs text-ink-400 inline-flex items-center gap-1.5">
              <span className="font-medium text-ink-500">{t('factory.established')}</span>
              {t(factory.established ?? 'placeholder.tbd')}
            </span>
            <span className="text-xs text-ink-400 inline-flex items-center gap-1.5">
              <span className="font-medium text-ink-500">{t('factory.capacity')}</span>
              {t(factory.capacity ?? 'placeholder.tbd')}
            </span>
          </div>
        </div>

        {/* Main Products */}
        <div>
          <SectionTitle icon="ri-box-3-line">{t('factory.products')}</SectionTitle>
          <ul className="space-y-2">
            {factory.productKeys.map((key) => (
              <li key={key} className="flex items-start gap-2.5 text-sm text-ink-500 leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full bg-warm-500 mt-2 shrink-0" />
                {t(key)}
              </li>
            ))}
          </ul>
        </div>

        {/* Specialized Technology */}
        <div>
          <SectionTitle icon="ri-settings-3-line">{t('factory.technology')}</SectionTitle>
          <ul className="space-y-2">
            {factory.specialtyKeys.map((key) => (
              <li key={key} className="flex items-start gap-2.5 text-sm text-ink-500 leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full bg-warm-500 mt-2 shrink-0" />
                {t(key)}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </article>
  );
}